import React, { useEffect, useMemo, useRef, useState } from 'react'
import { facilityCurrencyOrFallback } from '../utils/facilityCurrency'
import { useNavigate } from 'react-router-dom'
import ContextSelectorBar from '../components/ContextSelectorBar'
import CurrentContextBar from '../components/CurrentContextBar'
import DateInput from '../components/DateInput'
import Drawer from '../components/Drawer'
import GradientButton from '../components/GradientButton'
import PageShell from '../components/PageShell'
import FormField from '../components/FormField'
import { useStoredPrincipal } from '../hooks/useStoredPrincipal'
import { ensureFarmList, readCurrentFarm, type WizardFarm } from '../utils/farmWizard'
import { feedLogApi } from '../utils/feedLogApi'
import { loadActiveContextSelection, persistActiveContextSelection } from '../utils/activeContextSelection'
import type { EntityBatchSyncStatus, EntityMutationResult, EntityMutationSyncState } from '../utils/entityMutationSync'
import type { InventoryCategory, InventoryItem, InventoryUnit } from '../types/inventory'
import { inventoryApi } from '../utils/inventoryApi'
import { deriveInventorySyncOutcome } from '../utils/inventorySyncOutcome'
import { normalizeFeedTypeKey as normalizeKey } from '../utils/inventoryFeedTypeMatch'
import { createId } from '../utils/id'
import {
  applyReceipt,
  isMixItem,
  readMix,
  readReceipts,
  validateMix,
  type FeedMix,
  type MixValidation
} from '../utils/feedMix'
import { taskApi } from '../utils/taskApi'
import { computeSyncBannerVisibility } from '../utils/syncBannerVisibility'
import { useT } from '../i18n'

// Rate window: how many days of recent Feed-event history to average for "Avg/day"
// and the "kg logged in last Nd" badge. Short on purpose so the rate reflects
// current consumption habits.
const USAGE_RATE_WINDOW_DAYS = 14
// Fetch window: how far back to load Feed events so the per-item "Estimated on
// hand" can subtract usage since the item's `receivedOn` date. Items whose
// `receivedOn` is older than this cap fall back to the cap edge — the UI marks
// the estimate as capped via tooltip.
const USAGE_FETCH_WINDOW_DAYS = 180

const hasWindow = typeof globalThis !== 'undefined' && typeof globalThis.window !== 'undefined'

const parseDecimal = (value: string): number | null => {
  const trimmed = value.trim().replaceAll(',', '.')
  if (!trimmed) return null
  const parsed = Number(trimmed)
  return Number.isFinite(parsed) ? parsed : null
}


const localTodayIsoDate = (): string => {
  const now = new Date()
  const year = now.getFullYear()
  const month = String(now.getMonth() + 1).padStart(2, '0')
  const day = String(now.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

const isPlainObject = (value: unknown): value is Record<string, unknown> => (
  typeof value === 'object' && value !== null && !Array.isArray(value)
)

const toOptionalIsoDate = (value: unknown): string | undefined => {
  if (typeof value !== 'string') return undefined
  const trimmed = value.trim()
  if (!trimmed) return undefined
  if (!/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) return undefined
  return trimmed
}

const readWarehouseDates = (item: InventoryItem): { receivedOn: string | null; expiresOn: string | null } => {
  const meta = isPlainObject(item.metadata) ? item.metadata : undefined
  const receivedFromMeta = toOptionalIsoDate(meta?.receivedOn)
  const expiresFromMeta = toOptionalIsoDate(meta?.expiresOn)
  const fallback = typeof item.createdAt === 'string' && item.createdAt.length >= 10 ? item.createdAt.slice(0, 10) : null
  return {
    receivedOn: receivedFromMeta ?? fallback,
    expiresOn: expiresFromMeta ?? null
  }
}

type FeedLogPoint = { recordedAtMs: number; kg: number }

type UsageTotals = {
  logsByFeedType: Record<string, FeedLogPoint[]>
  totalsKgByFeedTypeInRateWindow: Record<string, number>
  feedTypeLabelByKey: Record<string, string>
  fetchFromIso: string
  fetchedAtMs: number
}

// Office Plans v0 (pre-validation PR 6): Inventario is organized by category
// tabs; Alimento is the default (the crew's daily stream).
const CATEGORY_TABS: InventoryCategory[] = ['feed', 'chemical', 'equipment', 'other']

type MixDraftRow = { itemId: string; percent: string }

const emptyMixRows = (): MixDraftRow[] => [{ itemId: '', percent: '' }, { itemId: '', percent: '' }]

type NoticeTone = 'info' | 'success' | 'error'

type NoticeState = {
  tone: NoticeTone
  message: string
}

type DraftErrors = Partial<Record<'name' | 'onHand' | 'reorderPoint' | 'receivedOn' | 'expiresOn' | 'unitCost' | 'currency', string>>

// Findings #125 (rehearsal 2): the unit cost was only visible when editing an
// item. Shown per unit of the item ("12.50 PEN / kg").
const formatUnitCost = (item: { unitCostCents?: number; currency?: string; unit?: string }): string => (
  typeof item.unitCostCents === 'number' && Number.isFinite(item.unitCostCents)
    ? `${(item.unitCostCents / 100).toFixed(2)} ${item.currency ?? ''} / ${item.unit ?? ''}`.replace(/\s+/g, ' ').trim()
    : '—'
)

export default function Inventory() {
  const t = useT()
  const navigate = useNavigate()
  const principal = useStoredPrincipal()
  const canEditInventory = !principal || principal.role === 'editor' || principal.role === 'admin'
  const canCreateTasks = principal?.role !== 'viewer'
  const taskCreateBlockedTooltip = canCreateTasks ? undefined : t('warehouse.taskCreateViewerBlockedTooltip')

  const [notice, setNotice] = useState<NoticeState | null>(null)
  const noticeVersionRef = useRef(0)
  const pushNotice = (tone: NoticeTone, message: string) => {
    noticeVersionRef.current += 1
    setNotice({ tone, message })
  }

  const getInventoryNoticeKey = (verb: 'saved' | 'deleted', state: EntityMutationSyncState) => {
    if (verb === 'saved') {
      if (state === 'synced') return 'warehouse.alertItemSavedSynced'
      if (state === 'offline') return 'warehouse.alertItemSavedOffline'
      return 'warehouse.alertItemSavedLocal'
    }
    if (state === 'synced') return 'warehouse.alertItemDeletedSynced'
    if (state === 'offline') return 'warehouse.alertItemDeletedOffline'
    return 'warehouse.alertItemDeletedLocal'
  }

  const applyInventoryNotice = (
    verb: 'saved' | 'deleted',
    result: Pick<EntityMutationResult<unknown>, 'syncState' | 'syncPromise'>
  ) => {
    const nextVersion = noticeVersionRef.current + 1
    noticeVersionRef.current = nextVersion
    setNotice({
      tone: result.syncState === 'synced' ? 'success' : 'info',
      message: t(getInventoryNoticeKey(verb, result.syncState))
    })

    if (!result.syncPromise) return

    void result.syncPromise.then((nextState) => {
      if (noticeVersionRef.current !== nextVersion) return
      if (nextState === result.syncState) return
      setNotice({
        tone: nextState === 'synced' ? 'success' : 'info',
        message: t(getInventoryNoticeKey(verb, nextState))
      })
    })
  }

  const [farms, setFarms] = useState<WizardFarm[]>([])
  const [farmId, setFarmId] = useState<string>('')

  const [items, setItems] = useState<InventoryItem[]>([])
  const [itemsLoading, setItemsLoading] = useState(false)
  const [itemsError, setItemsError] = useState<string | null>(null)
  const [syncStatus, setSyncStatus] = useState<EntityBatchSyncStatus | null>(null)
  const [syncLastResultSummary, setSyncLastResultSummary] = useState<{ resultLabel: string; attemptedAtLabel: string } | null>(null)
  const [retryingSync, setRetryingSync] = useState(false)
  const [refreshingSync, setRefreshingSync] = useState(false)
  const [usage, setUsage] = useState<UsageTotals | null>(null)
  const [usageError, setUsageError] = useState<string | null>(null)
  const [usageLoading, setUsageLoading] = useState(false)

  const [editingId, setEditingId] = useState<string | null>(null)
  const [draftName, setDraftName] = useState('')
  const [draftCategory, setDraftCategory] = useState<InventoryCategory>('feed')
  const [draftUnit, setDraftUnit] = useState<InventoryUnit>('kg')
  const [draftOnHand, setDraftOnHand] = useState('')
  const [draftReorderPoint, setDraftReorderPoint] = useState('')
  const [draftReceivedOn, setDraftReceivedOn] = useState(() => localTodayIsoDate())
  const [draftExpiresOn, setDraftExpiresOn] = useState('')
  const [draftUsageFeedType, setDraftUsageFeedType] = useState('')
  const [draftUnitCostMajor, setDraftUnitCostMajor] = useState('')
  const [draftUnitCostCurrency, setDraftUnitCostCurrency] = useState('USD')
  const [draftNotes, setDraftNotes] = useState('')
  const [draftErrors, setDraftErrors] = useState<DraftErrors>({})

  const [creatingTaskId, setCreatingTaskId] = useState<string | null>(null)
  const [pendingDeleteId, setPendingDeleteId] = useState<string | null>(null)

  const [categoryTab, setCategoryTab] = useState<InventoryCategory>('feed')
  const [formOpen, setFormOpen] = useState(false)

  // "Recibir": a delivery kept as history (metadata.receipts) that adds to stock.
  const [receiveItemId, setReceiveItemId] = useState<string | null>(null)
  const [receiveDate, setReceiveDate] = useState(() => localTodayIsoDate())
  const [receiveQuantity, setReceiveQuantity] = useState('')
  const [receiveSupplier, setReceiveSupplier] = useState('')
  const [receiveLotNumber, setReceiveLotNumber] = useState('')
  const [receiveUnitCost, setReceiveUnitCost] = useState('')
  const [receiveCurrency, setReceiveCurrency] = useState('USD')
  const [receiveExpiresOn, setReceiveExpiresOn] = useState('')
  const [receiveError, setReceiveError] = useState<string | null>(null)
  const [receiveSaving, setReceiveSaving] = useState(false)
  // Findings #144: a receipt is confirmed against a summary naming the
  // facility before it is saved (the facility is picked from a dropdown).
  const [receiveConfirming, setReceiveConfirming] = useState(false)

  // "Preparar mezcla": a recipe of 2+ feeds; no stock moves until it is fed.
  const [mixOpen, setMixOpen] = useState(false)
  const [mixEditingId, setMixEditingId] = useState<string | null>(null)
  const [mixName, setMixName] = useState('')
  const [mixRows, setMixRows] = useState<MixDraftRow[]>(() => emptyMixRows())
  const [mixNotes, setMixNotes] = useState('')
  const [mixError, setMixError] = useState<string | null>(null)

  const [isCompactLayout, setIsCompactLayout] = useState(() =>
    hasWindow ? (globalThis.window?.matchMedia?.('(max-width: 900px)')?.matches ?? false) : false
  )

  useEffect(() => {
    let cancelled = false

    const load = async () => {
      const list = await ensureFarmList()
      if (cancelled) return
      setFarms(list)

      const remembered = readCurrentFarm()
      const rememberedId = remembered?.id ? String(remembered.id) : ''

      const activeContext = loadActiveContextSelection()
      const activeFarmId = activeContext.farmId
      const activeFarmCandidate = (activeFarmId && list.some((farm) => farm.id === activeFarmId)) ? activeFarmId : ''

      const rememberedCandidate = (rememberedId && list.some((farm) => farm.id === rememberedId))
        ? rememberedId
        : ''

      const initialFarmId = activeFarmCandidate || rememberedCandidate || (list[0]?.id ?? '')
      setFarmId((prev) => prev || initialFarmId)
    }

    void load()
    return () => {
      cancelled = true
    }
  }, [])

  useEffect(() => {
    if (!hasWindow) return

    const query = globalThis.window?.matchMedia?.('(max-width: 900px)')
    if (!query) return

    const handleChange = (event: MediaQueryListEvent) => {
      setIsCompactLayout(event.matches)
    }

    setIsCompactLayout(query.matches)

    if (typeof query.addEventListener === 'function') {
      query.addEventListener('change', handleChange)
      return () => query.removeEventListener('change', handleChange)
    }

    query.addListener(handleChange)
    return () => query.removeListener(handleChange)
  }, [])

  useEffect(() => {
    let cancelled = false

    const loadItemsOnce = async () => {
      setItemsLoading(true)
      setItemsError(null)
      try {
        const next = await inventoryApi.listAll()
        if (cancelled) return
        setItems(next)
      } catch (error) {
        if (import.meta.env.DEV) {
          console.warn('[warehouse] failed to load inventory items', error)
        }
        if (cancelled) return
        setItems([])
        setItemsError(t('warehouse.alertSaveFailed'))
      } finally {
        if (!cancelled) setItemsLoading(false)
      }
    }

    void loadItemsOnce()
    return () => {
      cancelled = true
    }
  }, [t])

  useEffect(() => {
    if (!farmId) return
    persistActiveContextSelection({ farmId })
  }, [farmId])

  // Items are loaded once (tenant-scoped via Dexie); the facility filter is applied client-side.

  const selectedFarm = useMemo(() => farms.find((farm) => String(farm.id) === String(farmId)), [farms, farmId])
  const hasSelectedFarm = farmId.trim().length > 0

  const selectedFarmLabel = useMemo(() => {
    if (!farmId) return '—'
    return selectedFarm?.name || String(farmId)
  }, [farmId, selectedFarm])

  const formatCategory = (value: InventoryCategory): string => {
    if (value === 'feed') return t('warehouse.category.feed')
    if (value === 'chemical') return t('warehouse.category.chemical')
    if (value === 'equipment') return t('warehouse.category.equipment')
    return t('warehouse.category.other')
  }

  const farmOptions = useMemo(
    () => farms.map((farm) => ({ value: String(farm.id), label: farm.name || String(farm.id) })),
    [farms]
  )

  const visibleItems = useMemo(() => {
    const scoped = items.filter((item) => item.farmId === farmId)
    return scoped.sort((a, b) => a.name.localeCompare(b.name))
  }, [items, farmId])

  const visibleItemIds = useMemo(
    () => visibleItems.map((item) => item.id).filter(Boolean),
    [visibleItems]
  )

  const tabItems = useMemo(
    () => visibleItems.filter((item) => item.category === categoryTab),
    [visibleItems, categoryTab]
  )

  const farmFeedItems = useMemo(
    () => visibleItems.filter((item) => item.category === 'feed'),
    [visibleItems]
  )

  // Mix components are plain feeds (never another mix).
  const mixComponentOptions = useMemo(
    () => farmFeedItems.filter((item) => !isMixItem(item)),
    [farmFeedItems]
  )

  const itemNameById = useMemo(
    () => new Map(visibleItems.map((item) => [item.id, item.name])),
    [visibleItems]
  )

  const mixRecipeLabel = (mix: FeedMix): string => mix.components
    .map((component) => `${formatQty(component.percent, 2)} % ${itemNameById.get(component.itemId) ?? t('warehouse.mix.missingFeed')}`)
    .join(' + ')

  const syncAttentionMessage = useMemo(() => {
    if (!syncStatus || syncStatus.syncState === 'synced') return null

    if (syncStatus.blockedCount > 0) {
      if (syncStatus.errorMessage) {
        return t('warehouse.sync.blockedWithReason', {
          count: syncStatus.blockedCount,
          reason: syncStatus.errorMessage
        })
      }

      return t('warehouse.sync.blocked', { count: syncStatus.blockedCount })
    }

    if (syncStatus.syncState === 'offline') {
      return t('warehouse.sync.offline')
    }

    return t('warehouse.sync.pending')
  }, [syncStatus, t])

  // v2.1 finding #80: Warehouse co-renders the same three independent sync-
  // status JSX blocks as the Biomass / Feed / Tasks / EditFarm / Lots
  // surfaces migrated in finding #58 Plan A: (1) the transient `notice`
  // toast published via `pushNotice`, (2) the `syncLastResultSummary`
  // block, and (3) the `syncAttentionMessage` attention banner. The
  // Plan A audit missed this because it looked for a `*StatusMessage`
  // state; Warehouse's transient toast is spelled `notice`/`pushNotice`.
  // Gate the transient toast through the shared supersede rule so it
  // never coexists with the attention banner or last-result summary.
  //
  // Special-case: Inventory's `notice` state doubles as a validation-error
  // channel (e.g. "Select facility first"). Error notices are not
  // duplicates of the sync attention banner and must render regardless of
  // banner state, so they're excluded from the `hasActionMessage` input
  // and the render gate re-admits them explicitly.
  const bannerVisibility = computeSyncBannerVisibility({
    hasActionMessage: notice !== null && notice.tone !== 'error',
    hasAttentionMessage: syncAttentionMessage !== null,
    hasLastResultSummary: syncLastResultSummary !== null
  })

  useEffect(() => {
    let active = true

    if (!farmId.trim()) {
      setSyncStatus(null)
      return () => {
        active = false
      }
    }

    void (async () => {
      const status = await inventoryApi.readInventoryItemSyncStatus(visibleItemIds)
      if (!active) return
      setSyncStatus(status)
    })()

    return () => {
      active = false
    }
  }, [farmId, visibleItemIds])

  useEffect(() => {
    let cancelled = false

    const loadUsage = async () => {
      setUsageLoading(true)
      setUsageError(null)
      setUsage(null)

      const scopedFarmId = farmId.trim()
      if (!scopedFarmId) return

      const now = Date.now()
      const fetchFromMs = now - USAGE_FETCH_WINDOW_DAYS * 24 * 60 * 60 * 1000
      const rateCutoffMs = now - USAGE_RATE_WINDOW_DAYS * 24 * 60 * 60 * 1000
      const from = new Date(fetchFromMs).toISOString()

      try {
        // Findings #144: Inventario is the facility's store (GLOBALG.A.P. / ASC /
        // BAP: stock per storage place), so usage is the whole facility's — a
        // unit filter subtracted one unit's feed from the facility stock.
        const logs = await feedLogApi.list({
          farmId: scopedFarmId,
          fromRecordedAt: from
        })

        if (cancelled) return

        const logsByFeedType: Record<string, FeedLogPoint[]> = {}
        const rateTotals: Record<string, number> = {}
        const labels: Record<string, string> = {}
        for (const log of logs) {
          const feedType = (log.feedType ?? '').trim()
          const key = feedType ? normalizeKey(feedType) : ''
          if (!key) continue

          if (!labels[key]) {
            labels[key] = feedType
          }

          const kg = parseDecimal(String(log.feedKg ?? ''))
          if (kg === null || kg <= 0) continue

          const recordedMs = log.recordedAt ? Date.parse(log.recordedAt) : Number.NaN
          if (!Number.isFinite(recordedMs)) continue

          if (!logsByFeedType[key]) logsByFeedType[key] = []
          logsByFeedType[key].push({ recordedAtMs: recordedMs, kg })

          if (recordedMs >= rateCutoffMs) {
            rateTotals[key] = (rateTotals[key] ?? 0) + kg
          }
        }

        setUsage({
          logsByFeedType,
          totalsKgByFeedTypeInRateWindow: rateTotals,
          feedTypeLabelByKey: labels,
          fetchFromIso: from,
          fetchedAtMs: now
        })
      } catch (error) {
        console.warn('[warehouse] failed to derive usage from feed logs', error)
        if (cancelled) return
        setUsageError(t('warehouse.usageErrorUnable'))
      } finally {
        if (!cancelled) setUsageLoading(false)
      }
    }

    void loadUsage()
    return () => {
      cancelled = true
    }
  }, [farmId])

  const avgDailyKgFor = (item: InventoryItem): number | null => {
    if (item.category !== 'feed' || isMixItem(item)) return null
    if (item.unit !== 'kg') return null
    const feedType = item.usageFeedType?.trim() || item.name.trim()
    const key = normalizeKey(feedType)
    if (!key) return null
    const totalKg = usage?.totalsKgByFeedTypeInRateWindow[key]
    if (typeof totalKg !== 'number' || !Number.isFinite(totalKg) || totalKg <= 0) return 0
    return totalKg / USAGE_RATE_WINDOW_DAYS
  }

  const usageInfoFor = (item: InventoryItem): {
    key: string
    rawFeedType: string
    displayFeedType: string
    totalKgInWindow: number
  } | null => {
    if (item.category !== 'feed' || isMixItem(item)) return null
    if (item.unit !== 'kg') return null

    const rawFeedType = item.usageFeedType?.trim() || item.name.trim()
    const key = normalizeKey(rawFeedType)
    if (!key) return null

    const totalKgInWindow = usage?.totalsKgByFeedTypeInRateWindow[key] ?? 0
    const displayFeedType = usage?.feedTypeLabelByKey[key] || rawFeedType

    return {
      key,
      rawFeedType,
      displayFeedType,
      totalKgInWindow: typeof totalKgInWindow === 'number' && Number.isFinite(totalKgInWindow)
        ? totalKgInWindow
        : 0
    }
  }

  const suggestedFeedTypes = useMemo(() => {
    if (!usage) return [] as string[]
    const values = Object.values(usage.feedTypeLabelByKey)
      .map((value) => value.trim())
      .filter(Boolean)

    // Stable uniqueness + sort.
    const seen = new Set<string>()
    const unique: string[] = []
    for (const value of values) {
      const normalized = value.toLowerCase()
      if (seen.has(normalized)) continue
      seen.add(normalized)
      unique.push(value)
    }

    return unique.sort((a, b) => a.localeCompare(b))
  }, [usage])

  const daysOfCoverFor = (item: InventoryItem): number | null => {
    const daily = avgDailyKgFor(item)
    if (daily === null) return null
    if (daily <= 0) return null
    return item.onHand / daily
  }

  // Returns the anchor date used by `estimatedOnHandFor` for the given item, plus
  // whether the anchor was capped by the fetch window (i.e. the item's stored
  // `receivedOn` predates `USAGE_FETCH_WINDOW_DAYS` days ago, so we only
  // subtracted usage since the cap edge instead of since true receipt).
  const estimatedOnHandAnchorFor = (item: InventoryItem): {
    anchorIso: string
    capped: boolean
    receivedOnIso: string | null
  } | null => {
    if (item.category !== 'feed' || isMixItem(item)) return null
    if (item.unit !== 'kg') return null

    const dates = readWarehouseDates(item)
    const now = Date.now()
    const fetchCutoffMs = now - USAGE_FETCH_WINDOW_DAYS * 24 * 60 * 60 * 1000

    let anchorMs = fetchCutoffMs
    let capped = true
    if (dates.receivedOn) {
      const parsed = Date.parse(`${dates.receivedOn}T00:00:00`)
      if (Number.isFinite(parsed)) {
        if (parsed >= fetchCutoffMs) {
          anchorMs = parsed
          capped = false
        }
      }
    }

    return {
      anchorIso: new Date(anchorMs).toISOString().slice(0, 10),
      capped,
      receivedOnIso: dates.receivedOn
    }
  }

  const estimatedOnHandFor = (item: InventoryItem): number | null => {
    if (item.category !== 'feed') return null
    if (item.unit !== 'kg') return null
    const info = usageInfoFor(item)
    if (!info) return null
    const anchor = estimatedOnHandAnchorFor(item)
    if (!anchor) return null

    const cutoffMs = Date.parse(`${anchor.anchorIso}T00:00:00`)
    if (!Number.isFinite(cutoffMs)) return null

    // Anchor the subtraction to the item's `receivedOn` (capped at the fetch
    // window). This replaces the legacy "last 14 days" bug where the estimate
    // only reflected 14 days of consumption regardless of when stock was
    // recorded.
    const logs = usage?.logsByFeedType[info.key] ?? []
    let usedSinceAnchorKg = 0
    for (const point of logs) {
      if (point.recordedAtMs >= cutoffMs) {
        usedSinceAnchorKg += point.kg
      }
    }

    const estimated = item.onHand - usedSinceAnchorKg
    if (!Number.isFinite(estimated)) return null
    return Math.max(0, estimated)
  }

  const reorderStatusFor = (item: InventoryItem): {
    shouldReorder: boolean
    reason: 'manual' | 'estimated' | null
    estimatedOnHand: number | null
  } => {
    if (item.reorderPoint === undefined) {
      return { shouldReorder: false, reason: null, estimatedOnHand: null }
    }

    const estimatedOnHand = estimatedOnHandFor(item)

    const manual = item.onHand <= item.reorderPoint
    if (manual) {
      return { shouldReorder: true, reason: 'manual', estimatedOnHand }
    }

    const estimated = estimatedOnHand !== null && estimatedOnHand <= item.reorderPoint
    if (estimated) {
      return { shouldReorder: true, reason: 'estimated', estimatedOnHand }
    }

    return { shouldReorder: false, reason: null, estimatedOnHand }
  }

  const createReorderTaskFor = async (item: InventoryItem, reorder: ReturnType<typeof reorderStatusFor>) => {
    if (!canCreateTasks) {
      pushNotice('error', t('warehouse.alertViewerCannotCreateTask'))
      return
    }

    if (creatingTaskId) return
    setCreatingTaskId(item.id)

    try {
      const tasks = await taskApi.listTasks()

      const existing = tasks.find((task) => {
        if (task.completed) return false
        const meta = task.metadata
        if (!meta || typeof meta !== 'object' || Array.isArray(meta)) return false
        return meta.kind === 'inventoryReorder' && meta.inventoryItemId === item.id
      })

      if (existing) {
        navigate('/tasks?flash=warehouseReorderTaskExists')
        return
      }

      const reorderPointText = item.reorderPoint === undefined ? '—' : `${item.reorderPoint} ${item.unit}`
      const estimatedText = reorder.reason === 'estimated' && reorder.estimatedOnHand !== null
        ? `\n${t('warehouse.estimatedOnHandLabel')} ${reorder.estimatedOnHand.toFixed(2)} ${item.unit}`
        : ''

      const description = t('warehouse.taskDescriptionReorder', {
        item: item.name,
        onHand: item.onHand.toLocaleString(undefined, { maximumFractionDigits: 3 }),
        unit: item.unit,
        reorderPoint: reorderPointText,
        estimatedOnHand: estimatedText,
        capDays: USAGE_FETCH_WINDOW_DAYS
      })

      await taskApi.createTask({
        title: t('warehouse.taskTitleReorder', { item: item.name }),
        description,
        metadata: {
          kind: 'inventoryReorder',
          inventoryItemId: item.id,
          inventoryItemName: item.name,
          farmId: farmId.trim() || undefined,
          reorderPoint: item.reorderPoint,
          onHand: item.onHand,
          estimatedOnHand: reorder.estimatedOnHand,
          usageFetchWindowDays: USAGE_FETCH_WINDOW_DAYS,
          usageRateWindowDays: USAGE_RATE_WINDOW_DAYS
        }
      })

      navigate('/tasks?flash=warehouseTaskCreated')
    } catch (error) {
      console.warn('[warehouse] failed to create reorder task', error)
      pushNotice('error', t('warehouse.alertTaskCreateFailed'))
    } finally {
      setCreatingTaskId(null)
    }
  }

  const reorderTaskActionFor = (item: InventoryItem, reorder: ReturnType<typeof reorderStatusFor>) => {
    if (!reorder.shouldReorder) return null

    return (
      <div style={{ marginTop: 6, display: 'flex', justifyContent: 'flex-end' }}>
        <GradientButton
          variant="success"
          size="xxs"
          onClick={() => void createReorderTaskFor(item, reorder)}
          disabled={!canCreateTasks || creatingTaskId === item.id}
          title={taskCreateBlockedTooltip}
        >
          {creatingTaskId === item.id ? t('warehouse.button.creatingTask') : t('warehouse.button.createTask')}
        </GradientButton>
      </div>
    )
  }

  const formatQty = (value: number, maxFractionDigits = 2): string => {
    return value.toLocaleString(undefined, { maximumFractionDigits: maxFractionDigits })
  }

  const renderItemActions = (item: InventoryItem) => {
    if (pendingDeleteId === item.id) {
      return (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 6 }}>
          <div className="responsive-actions" style={{ justifyContent: 'flex-end' }}>
            <GradientButton
              variant="secondary"
              size="xs"
              onClick={cancelPendingDelete}
            >
              {t('warehouse.button.cancel')}
            </GradientButton>
            <GradientButton
              variant="danger"
              size="xs"
              onClick={() => removeItem(item.id)}
              disabled={!canEditInventory}
              title={t('warehouse.confirmDelete')}
            >
              {t('warehouse.button.delete')}
            </GradientButton>
          </div>
          <div style={{ fontSize: 12, color: '#6b7280', maxWidth: 240, textAlign: 'right' }}>
            {t('warehouse.confirmDelete')}
          </div>
        </div>
      )
    }

    const isMix = isMixItem(item)
    return (
      <div className="responsive-actions" style={{ justifyContent: 'flex-end' }}>
        {!isMix ? (
          <GradientButton
            variant="success"
            size="xs"
            onClick={() => openReceive(item)}
            disabled={!canEditInventory}
            data-testid={`warehouse-receive-${item.id}`}
          >
            {t('warehouse.button.receive')}
          </GradientButton>
        ) : null}
        <GradientButton variant="info" size="xs" onClick={() => (isMix ? startEditMix(item) : startEdit(item))}>
          {t('warehouse.button.edit')}
        </GradientButton>
        <GradientButton
          variant="danger"
          size="xs"
          onClick={() => removeItem(item.id)}
          disabled={!canEditInventory}
        >
          {t('warehouse.button.delete')}
        </GradientButton>
      </div>
    )
  }

  const resetDraft = () => {
    setEditingId(null)
    setDraftName('')
    setDraftCategory('feed')
    setDraftUnit('kg')
    setDraftOnHand('')
    setDraftReorderPoint('')
    setDraftReceivedOn(localTodayIsoDate())
    setDraftExpiresOn('')
    setDraftUsageFeedType('')
    setDraftUnitCostMajor('')
    setDraftUnitCostCurrency(facilityCurrencyOrFallback(selectedFarm))
    setDraftNotes('')
    setDraftErrors({})
    setFormOpen(false)
  }

  const openNewItem = () => {
    resetDraft()
    setDraftCategory(categoryTab)
    setDraftUnit(categoryTab === 'feed' ? 'kg' : categoryTab === 'chemical' ? 'l' : 'pcs')
    setPendingDeleteId(null)
    setFormOpen(true)
  }

  const closeReceive = () => {
    setReceiveConfirming(false)
    setReceiveItemId(null)
    setReceiveError(null)
    setReceiveSaving(false)
  }

  const openReceive = (item: InventoryItem) => {
    setReceiveItemId(item.id)
    setReceiveDate(localTodayIsoDate())
    setReceiveQuantity('')
    setReceiveSupplier('')
    setReceiveLotNumber('')
    setReceiveUnitCost(
      typeof item.unitCostCents === 'number' && Number.isInteger(item.unitCostCents) && item.unitCostCents >= 0
        ? (item.unitCostCents / 100).toFixed(2)
        : ''
    )
    setReceiveCurrency(item.currency?.trim() ? item.currency.trim().toUpperCase() : facilityCurrencyOrFallback(selectedFarm))
    setReceiveExpiresOn('')
    setReceiveError(null)
    setReceiveSaving(false)
    setPendingDeleteId(null)
  }

  const receiveItem = receiveItemId ? items.find((item) => item.id === receiveItemId) ?? null : null

  // Feed used between the item's current received-on date and the delivery
  // date, so the stock carried into the new delivery matches the estimate.
  const feedUsedBetween = async (item: InventoryItem, fromIso: string, toIso: string): Promise<number> => {
    if (item.category !== 'feed' || item.unit !== 'kg') return 0
    const fromMs = Date.parse(`${fromIso}T00:00:00`)
    const toMs = Date.parse(`${toIso}T00:00:00`)
    if (!Number.isFinite(fromMs) || !Number.isFinite(toMs) || toMs <= fromMs) return 0
    const key = normalizeKey(item.usageFeedType?.trim() || item.name.trim())
    if (!key) return 0
    const logs = await feedLogApi.list({ farmId: item.farmId, fromRecordedAt: new Date(fromMs).toISOString() })
    let used = 0
    for (const log of logs) {
      if (normalizeKey(String(log.feedType ?? '')) !== key) continue
      const recordedMs = log.recordedAt ? Date.parse(log.recordedAt) : Number.NaN
      if (!Number.isFinite(recordedMs) || recordedMs < fromMs || recordedMs >= toMs) continue
      const kg = parseDecimal(String(log.feedKg ?? ''))
      if (kg !== null && kg > 0) used += kg
    }
    return used
  }

  const saveReceive = async () => {
    if (!canEditInventory || !receiveItem || receiveSaving) return
    const date = toOptionalIsoDate(receiveDate)
    if (!date) {
      setReceiveError(t('warehouse.receive.error.date'))
      return
    }
    const quantity = parseDecimal(receiveQuantity)
    if (quantity === null || quantity <= 0) {
      setReceiveError(t('warehouse.receive.error.quantity'))
      return
    }
    let unitCostCents: number | undefined
    let currency: string | undefined
    if (receiveUnitCost.trim()) {
      const major = parseDecimal(receiveUnitCost)
      if (major === null || major < 0) {
        setReceiveError(t('warehouse.alertUnitCostInvalid'))
        return
      }
      const code = receiveCurrency.trim().toUpperCase()
      if (!/^[A-Z]{3}$/.test(code)) {
        setReceiveError(t('warehouse.alertCurrencyInvalid'))
        return
      }
      unitCostCents = Math.round(major * 100)
      currency = code
    }
    const expiresOn = toOptionalIsoDate(receiveExpiresOn)
    if (expiresOn && expiresOn < date) {
      setReceiveError(t('warehouse.alertExpiresBeforeReceived'))
      return
    }
    setReceiveError(null)
    setReceiveSaving(true)
    try {
      const previousReceivedOn = readWarehouseDates(receiveItem).receivedOn
      const used = previousReceivedOn && date >= previousReceivedOn
        ? await feedUsedBetween(receiveItem, previousReceivedOn, date)
        : 0
      const updated = applyReceipt(receiveItem, {
        id: createId('rcv'),
        date,
        quantity,
        ...(receiveSupplier.trim() ? { supplier: receiveSupplier.trim() } : {}),
        ...(receiveLotNumber.trim() ? { lotNumber: receiveLotNumber.trim() } : {}),
        ...(unitCostCents !== undefined ? { unitCostCents } : {}),
        ...(currency ? { currency } : {}),
        ...(expiresOn ? { expiresOn } : {})
      }, new Date().toISOString(), used)
      const result = await inventoryApi.upsert(updated)
      setItems(result.data)
      closeReceive()
      applyInventoryNotice('saved', result)
    } catch (error) {
      console.warn('[warehouse] failed to record delivery', error)
      setReceiveSaving(false)
      pushNotice('error', t('warehouse.alertSaveFailed'))
    }
  }

  const closeMix = () => {
    setMixOpen(false)
    setMixEditingId(null)
    setMixName('')
    setMixRows(emptyMixRows())
    setMixNotes('')
    setMixError(null)
  }

  const openNewMix = () => {
    closeMix()
    setPendingDeleteId(null)
    setMixOpen(true)
  }

  const startEditMix = (item: InventoryItem) => {
    const mix = readMix(item)
    setMixEditingId(item.id)
    setMixName(item.name)
    setMixRows(mix && mix.components.length > 0
      ? mix.components.map((component) => ({ itemId: component.itemId, percent: String(component.percent) }))
      : emptyMixRows())
    setMixNotes(item.notes ?? '')
    setMixError(null)
    setPendingDeleteId(null)
    setMixOpen(true)
  }

  const mixPercentTotal = mixRows.reduce((total, row) => total + (parseDecimal(row.percent) ?? 0), 0)

  const mixErrorMessage = (reason: Exclude<MixValidation, { ok: true }>['reason']): string => {
    if (reason === 'tooFewComponents') return t('warehouse.mix.error.tooFew')
    if (reason === 'duplicateComponent') return t('warehouse.mix.error.duplicate')
    if (reason === 'invalidPercent') return t('warehouse.mix.error.percent')
    if (reason === 'sumNot100') return t('warehouse.mix.error.sum')
    return t('warehouse.mix.error.unknownFeed')
  }

  const saveMix = async () => {
    if (!canEditInventory) return
    const scopedFarmId = farmId.trim()
    if (!scopedFarmId) {
      setMixError(t('warehouse.alertSelectFacilityFirst'))
      return
    }
    const name = mixName.trim()
    if (!name) {
      setMixError(t('warehouse.alertItemNameRequired'))
      return
    }
    const nameKey = normalizeKey(name)
    const taken = visibleItems.some((item) => item.id !== mixEditingId && (
      normalizeKey(item.name) === nameKey || normalizeKey(item.usageFeedType ?? '') === nameKey
    ))
    if (taken) {
      setMixError(t('warehouse.mix.error.nameTaken'))
      return
    }
    const mix: FeedMix = {
      components: mixRows
        .filter((row) => row.itemId || row.percent.trim())
        .map((row) => ({ itemId: row.itemId, percent: parseDecimal(row.percent) ?? Number.NaN }))
    }
    const validation = validateMix(mix, farmFeedItems)
    if (!validation.ok) {
      setMixError(mixErrorMessage(validation.reason))
      return
    }
    setMixError(null)
    const timestamp = new Date().toISOString()
    const existing = mixEditingId ? items.find((item) => item.id === mixEditingId) : undefined
    const next: InventoryItem = existing
      ? {
        ...existing,
        name,
        notes: mixNotes.trim() || undefined,
        metadata: { ...(isPlainObject(existing.metadata) ? existing.metadata : {}), mix },
        updatedAt: timestamp
      }
      : {
        id: createId('inv'),
        farmId: scopedFarmId,
        name,
        category: 'feed',
        unit: 'kg',
        onHand: 0,
        notes: mixNotes.trim() || undefined,
        metadata: { receivedOn: localTodayIsoDate(), mix },
        createdAt: timestamp,
        updatedAt: timestamp
      }
    try {
      const result = await inventoryApi.upsert(next)
      setItems(result.data)
      closeMix()
      applyInventoryNotice('saved', result)
    } catch (error) {
      console.warn('[warehouse] failed to save mix', error)
      pushNotice('error', t('warehouse.alertSaveFailed'))
    }
  }

  const startEdit = (item: InventoryItem) => {
    const dates = readWarehouseDates(item)
    setEditingId(item.id)
    setDraftName(item.name)
    setDraftCategory(item.category)
    setDraftUnit(item.unit)
    setDraftOnHand(item.onHand.toString())
    setDraftReorderPoint(item.reorderPoint?.toString() ?? '')
    setDraftReceivedOn(dates.receivedOn ?? localTodayIsoDate())
    setDraftExpiresOn(dates.expiresOn ?? '')
    setDraftUsageFeedType(item.usageFeedType ?? '')
    setDraftUnitCostMajor(
      typeof item.unitCostCents === 'number' && Number.isInteger(item.unitCostCents) && item.unitCostCents >= 0
        ? (item.unitCostCents / 100).toFixed(2)
        : ''
    )
    setDraftUnitCostCurrency(item.currency?.trim() ? item.currency.trim().toUpperCase() : facilityCurrencyOrFallback(selectedFarm))
    setDraftNotes(item.notes ?? '')
    setDraftErrors({})
    setPendingDeleteId(null)
    setFormOpen(true)
  }

  const saveDraft = () => {
    if (!canEditInventory) return

    setDraftErrors({})
    setNotice(null)

    const scopedFarmId = farmId.trim()
    if (!scopedFarmId) {
      pushNotice('error', t('warehouse.alertSelectFacilityFirst'))
      return
    }

    const name = draftName.trim()
    if (!name) {
      setDraftErrors({ name: t('warehouse.alertItemNameRequired') })
      return
    }

    const onHand = parseDecimal(draftOnHand)
    if (onHand === null || onHand < 0) {
      setDraftErrors({ onHand: t('warehouse.alertOnHandInvalid') })
      return
    }

    const reorderPoint = draftReorderPoint.trim() ? parseDecimal(draftReorderPoint) : null
    if (draftReorderPoint.trim() && (reorderPoint === null || reorderPoint < 0)) {
      setDraftErrors({ reorderPoint: t('warehouse.alertReorderInvalid') })
      return
    }

    const receivedOn = toOptionalIsoDate(draftReceivedOn)
    if (!receivedOn) {
      setDraftErrors({ receivedOn: t('warehouse.alertReceivedOnRequired') })
      return
    }

    const expiresOn = toOptionalIsoDate(draftExpiresOn)
    if (expiresOn && expiresOn < receivedOn) {
      setDraftErrors({ expiresOn: t('warehouse.alertExpiresBeforeReceived') })
      return
    }

    const normalizedReorderPoint = reorderPoint ?? undefined

    // Unit cost (every category since findings #105 — a bath books its cost
    // from the chemical's price). Empty string -> undefined so we can null out
    // an existing cost by clearing the field. Parsed in major units (e.g.
    // "1.25" for $1.25) and converted to integer cents.
    let unitCostCents: number | undefined
    let currencyCode: string | undefined
    {
      const trimmedCost = draftUnitCostMajor.trim()
      if (trimmedCost) {
        const parsedMajor = parseDecimal(trimmedCost)
        if (parsedMajor === null || parsedMajor < 0) {
          setDraftErrors({ unitCost: t('warehouse.alertUnitCostInvalid') })
          return
        }
        unitCostCents = Math.round(parsedMajor * 100)
        const normalizedCurrency = draftUnitCostCurrency.trim().toUpperCase()
        if (!/^[A-Z]{3}$/.test(normalizedCurrency)) {
          setDraftErrors({ currency: t('warehouse.alertCurrencyInvalid') })
          return
        }
        currencyCode = normalizedCurrency
      }
    }

    if (editingId) {
      const existing = items.find((item) => item.id === editingId)
      if (!existing) {
        pushNotice('error', t('warehouse.alertEditItemNotFound'))
        resetDraft()
        return
      }

      const updated: InventoryItem = {
        ...existing,
        farmId: scopedFarmId,
        name,
        category: draftCategory,
        unit: draftUnit,
        onHand,
        reorderPoint: normalizedReorderPoint,
        usageFeedType: draftUsageFeedType.trim() || undefined,
        unitCostCents,
        currency: currencyCode,
        notes: draftNotes.trim() || undefined,
        metadata: {
          ...(isPlainObject(existing.metadata) ? existing.metadata : {}),
          receivedOn,
          ...(expiresOn ? { expiresOn } : {})
        }
      }

      void inventoryApi.upsert(updated)
        .then((result) => {
          setItems(result.data)
          resetDraft()
          applyInventoryNotice('saved', result)
        })
        .catch((error) => {
          console.warn('[warehouse] failed to save inventory item', error)
          pushNotice('error', t('warehouse.alertSaveFailed'))
        })

      return
    }

    void inventoryApi.create({
      farmId: scopedFarmId,
      name,
      category: draftCategory,
      unit: draftUnit,
      onHand,
      reorderPoint: normalizedReorderPoint,
      receivedOn,
      expiresOn: expiresOn ?? undefined,
      usageFeedType: draftUsageFeedType.trim() || undefined,
      unitCostCents,
      currency: currencyCode,
      notes: draftNotes.trim() || undefined
    })
      .then((result) => {
        setItems(result.data)
        resetDraft()
        applyInventoryNotice('saved', result)
      })
      .catch((error) => {
        console.warn('[warehouse] failed to add inventory item', error)
        pushNotice('error', t('warehouse.alertSaveFailed'))
      })
  }

  const removeItem = (id: string) => {
    if (!canEditInventory) return

    if (pendingDeleteId !== id) {
      setPendingDeleteId(id)
      return
    }

    setPendingDeleteId(null)

    void inventoryApi.delete(id)
      .then((result) => {
        setItems(result.data)
        if (editingId === id) resetDraft()
        applyInventoryNotice('deleted', result)
      })
      .catch((error) => {
        console.warn('[warehouse] failed to delete inventory item', error)
        pushNotice('error', t('warehouse.alertDeleteFailed'))
      })
  }

  const cancelPendingDelete = () => setPendingDeleteId(null)

  const goToFeed = () => {
    const scopedFarmId = farmId.trim()
    if (!scopedFarmId) {
      pushNotice('info', t('warehouse.alertSelectFacilityFirst'))
      return
    }

    const params = new URLSearchParams()
    params.set('farmId', scopedFarmId)
    if (selectedFarm?.name) params.set('farmName', selectedFarm.name)

    navigate(`/feed?${params.toString()}`)
  }

  const goToTasks = () => {
    navigate('/tasks')
  }

  const handleInventorySyncRefresh = async (): Promise<void> => {
    if (refreshingSync) return

    setRefreshingSync(true)
    try {
      const nextItems = await inventoryApi.listAll()
      setItems(nextItems)

      const scopedIds = nextItems
        .filter((item) => item.farmId === farmId)
        .map((item) => item.id)
        .filter(Boolean)

      const status = await inventoryApi.readInventoryItemSyncStatus(scopedIds)
      setSyncStatus(status)

      const outcome = deriveInventorySyncOutcome({
        status,
        skippedNotFoundCount: 0
      })

      setSyncLastResultSummary({
        resultLabel: t(outcome.resultKey, outcome.resultParams),
        attemptedAtLabel: new Date().toLocaleString()
      })
    } catch (error) {
      console.warn('[warehouse] failed to refresh sync status', error)
      pushNotice('error', t('warehouse.sync.refreshFailed'))
    } finally {
      setRefreshingSync(false)
    }
  }

  const handleInventorySyncRetry = async (): Promise<void> => {
    if (retryingSync) return

    setRetryingSync(true)
    try {
      const report = await inventoryApi.retryInventoryItemSyncStatusWithReport(visibleItemIds)
      setSyncStatus(report.status)

      const outcome = deriveInventorySyncOutcome({
        status: report.status,
        skippedNotFoundCount: report.skippedNotFoundCount
      })

      setSyncLastResultSummary({
        resultLabel: t(outcome.resultKey, outcome.resultParams),
        attemptedAtLabel: new Date().toLocaleString()
      })

      const nextItems = await inventoryApi.listAll()
      setItems(nextItems)
    } catch (error) {
      console.warn('[warehouse] failed to retry inventory sync', error)
      pushNotice('error', t('warehouse.sync.retryFailed'))
    } finally {
      setRetryingSync(false)
    }
  }

  const renderItemDetails = (item: InventoryItem) => {
    const mix = readMix(item)
    if (mix) {
      const valid = validateMix(mix, farmFeedItems).ok
      return (
        <div style={{ marginTop: 2, fontSize: 12, color: '#6b7280' }} data-testid={`warehouse-mix-recipe-${item.id}`}>
          <span style={{ fontWeight: 700, color: '#047857' }}>{t('warehouse.mix.badge')}</span>{' '}
          {mixRecipeLabel(mix)}
          {!valid ? (
            <div style={{ color: '#b91c1c', fontWeight: 700 }}>{t('warehouse.mix.invalidHint')}</div>
          ) : null}
        </div>
      )
    }
    const receipts = readReceipts(item)
    const latest = receipts[receipts.length - 1]
    if (!latest) return null
    return (
      <div style={{ marginTop: 2, fontSize: 12, color: '#6b7280' }} data-testid={`warehouse-latest-receipt-${item.id}`}>
        {t('warehouse.receive.latest', {
          date: latest.date,
          quantity: formatQty(latest.quantity, 3),
          unit: item.unit
        })}
        {latest.lotNumber ? ` · ${t('warehouse.receive.lotShort', { lot: latest.lotNumber })}` : ''}
        {latest.supplier ? ` · ${latest.supplier}` : ''}
      </div>
    )
  }

  const inventoryContent = (() => {
    if (farmId.trim().length === 0) {
      return <div style={{ fontSize: 14, color: '#6b7280' }}>{t('warehouse.emptySelectFacility')}</div>
    }

    if (itemsLoading) {
      return <div style={{ fontSize: 14, color: '#6b7280' }}>{t('feed.status.loading')}</div>
    }

    if (tabItems.length === 0) {
      return <div style={{ fontSize: 14, color: '#6b7280' }}>{t('warehouse.emptyNoItemsInTab')}</div>
    }

    if (isCompactLayout) {
      return (
        <div style={{ display: 'grid', gap: 12 }}>
          {tabItems.map((item) => {
            const daily = avgDailyKgFor(item)
            const days = daysOfCoverFor(item)
            const usageInfo = usageInfoFor(item)
            const reorder = reorderStatusFor(item)
            const estimatedOnHand = estimatedOnHandFor(item)
            const estimatedAnchor = estimatedOnHandAnchorFor(item)
            const estimatedTooltip = estimatedAnchor
              ? (estimatedAnchor.capped
                ? t('warehouse.estimatedOnHandTooltipCapped', {
                  anchorDate: estimatedAnchor.anchorIso,
                  capDays: USAGE_FETCH_WINDOW_DAYS
                })
                : t('warehouse.estimatedOnHandTooltip', {
                  anchorDate: estimatedAnchor.anchorIso
                }))
              : undefined
            const dates = readWarehouseDates(item)
            return (
              <div
                key={item.id}
                style={{
                  border: '1px solid #e2e8f0',
                  borderRadius: 12,
                  padding: 12,
                  background: '#ffffff'
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontWeight: 700, wordBreak: 'break-word' }}>{item.name}</div>
                    {renderItemDetails(item)}
                    <div style={{ marginTop: 2, fontSize: 12, color: '#6b7280' }}>{formatCategory(item.category)}</div>
                    <div style={{ marginTop: 4, fontSize: 12, color: '#6b7280' }}>
                      <span>
                        <strong style={{ color: '#0f172a' }}>{t('warehouse.field.receivedOn')}:</strong>{' '}
                        {dates.receivedOn ?? '—'}
                      </span>
                      <span>{' '}·{' '}</span>
                      <span>
                        <strong style={{ color: '#0f172a' }}>{t('warehouse.field.expiresOn')}:</strong>{' '}
                        {dates.expiresOn ?? '—'}
                      </span>
                    </div>
                    {item.notes ? <div style={{ marginTop: 6, fontSize: 12, color: '#6b7280' }}>{item.notes}</div> : null}
                  </div>

                  <div style={{ textAlign: 'right', flex: '0 0 auto' }}>
                    <div style={{ fontWeight: 700 }}>
                      {isMixItem(item) ? '—' : `${formatQty(item.onHand, 3)} ${item.unit}`}
                    </div>
                    <div style={{ marginTop: 2, fontSize: 12, color: '#6b7280' }}>
                      {t('warehouse.table.onHand')}
                    </div>
                    {!isMixItem(item) && typeof item.unitCostCents === 'number' ? (
                      <div style={{ marginTop: 4, fontSize: 12, color: '#374151' }}>{formatUnitCost(item)}</div>
                    ) : null}
                  </div>
                </div>

                {(usageInfo || daily !== null || estimatedOnHand !== null || days !== null) ? (
                  <div style={{ marginTop: 10, display: 'grid', gap: 6 }}>
                    {usageInfo ? (
                      <div style={{ fontSize: 12, color: '#6b7280' }}>
                        {t('warehouse.linkedFeedTypeLabel')}{' '}
                        <span style={{ color: '#374151', fontWeight: 600 }}>{usageInfo.displayFeedType}</span>
                        {usageInfo.totalKgInWindow > 0
                          ? ` · ${t('warehouse.linkedFeedTypeUsageInWindow', {
                            totalKg: formatQty(usageInfo.totalKgInWindow, 2),
                            days: USAGE_RATE_WINDOW_DAYS
                          })}`
                          : ` · ${t('warehouse.linkedFeedTypeNoUsageInWindow', { days: USAGE_RATE_WINDOW_DAYS })}`}
                      </div>
                    ) : null}

                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, fontSize: 12, color: '#475569' }}>
                      <span title={estimatedTooltip}>
                        <strong style={{ color: '#0f172a' }}>{t('warehouse.table.estimatedOnHand')}:</strong>{' '}
                        {estimatedOnHand === null ? '—' : `${formatQty(estimatedOnHand, 2)} ${item.unit}${estimatedAnchor?.capped ? ' *' : ''}`}
                      </span>
                      <span title={t('warehouse.table.avgPerDayTooltip', { days: USAGE_RATE_WINDOW_DAYS })}>
                        <strong style={{ color: '#0f172a' }}>{t('warehouse.table.avgPerDay')}:</strong>{' '}
                        {daily === null ? '—' : `${formatQty(daily, 2)} kg`}
                      </span>
                      <span>
                        <strong style={{ color: '#0f172a' }}>{t('warehouse.table.daysCover')}:</strong>{' '}
                        {days === null ? '—' : days.toFixed(1)}
                      </span>
                    </div>
                  </div>
                ) : null}

                <div style={{ marginTop: 10, display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'flex-start' }}>
                  <div style={{ fontSize: 12, color: '#475569' }}>
                    <div>
                      <strong style={{ color: '#0f172a' }}>{t('warehouse.table.reorder')}:</strong>{' '}
                      {item.reorderPoint === undefined ? '—' : `${formatQty(item.reorderPoint, 2)} ${item.unit}`}
                    </div>
                    {reorder.shouldReorder ? (
                      <div style={{ marginTop: 4, fontWeight: 700 }} title={t('warehouse.reorderNowTooltip')}>
                        {t('warehouse.reorderNow')}
                      </div>
                    ) : null}
                    {reorderTaskActionFor(item, reorder)}
                  </div>

                  <div style={{ marginLeft: 'auto' }}>
                    {renderItemActions(item)}
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      )
    }

    return (
      <div className="table-shell" style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }} aria-label={t('warehouse.section.inventory')}>
          <thead>
            <tr>
              <th
                scope="col"
                style={{
                  textAlign: 'left',
                  padding: '12px 10px',
                  borderBottom: '1px solid #e5e7eb',
                  position: 'sticky',
                  top: 0,
                  background: '#ffffff',
                  zIndex: 1,
                  minWidth: 260
                }}
              >
                {t('warehouse.table.item')}
              </th>
              <th
                scope="col"
                style={{
                  textAlign: 'left',
                  padding: '12px 10px',
                  borderBottom: '1px solid #e5e7eb',
                  position: 'sticky',
                  top: 0,
                  background: '#ffffff',
                  zIndex: 1,
                  minWidth: 130
                }}
              >
                {t('warehouse.table.category')}
              </th>
              <th
                scope="col"
                style={{
                  textAlign: 'left',
                  padding: '12px 10px',
                  borderBottom: '1px solid #e5e7eb',
                  position: 'sticky',
                  top: 0,
                  background: '#ffffff',
                  zIndex: 1,
                  minWidth: 130
                }}
              >
                {t('warehouse.field.receivedOn')}
              </th>
              <th
                scope="col"
                style={{
                  textAlign: 'left',
                  padding: '12px 10px',
                  borderBottom: '1px solid #e5e7eb',
                  position: 'sticky',
                  top: 0,
                  background: '#ffffff',
                  zIndex: 1,
                  minWidth: 130
                }}
              >
                {t('warehouse.field.expiresOn')}
              </th>
              <th
                scope="col"
                style={{
                  textAlign: 'right',
                  padding: '12px 10px',
                  borderBottom: '1px solid #e5e7eb',
                  position: 'sticky',
                  top: 0,
                  background: '#ffffff',
                  zIndex: 1,
                  minWidth: 150
                }}
              >
                {t('warehouse.table.onHand')}
              </th>
              <th scope="col" style={{ textAlign: 'right', padding: '12px 10px', borderBottom: '1px solid #e5e7eb', position: 'sticky', top: 0, background: '#ffffff', zIndex: 1, minWidth: 130 }}>
                {t('warehouse.table.unitCost')}
              </th>
              <th
                scope="col"
                style={{
                  textAlign: 'right',
                  padding: '12px 10px',
                  borderBottom: '1px solid #e5e7eb',
                  position: 'sticky',
                  top: 0,
                  background: '#ffffff',
                  zIndex: 1,
                  minWidth: 150
                }}
              >
                {t('warehouse.table.estimatedOnHand')}
              </th>
              <th
                scope="col"
                style={{ textAlign: 'right', padding: '10px 8px', borderBottom: '1px solid #e5e7eb' }}
                title={t('warehouse.table.avgPerDayTooltip', { days: USAGE_RATE_WINDOW_DAYS })}
              >
                {t('warehouse.table.avgPerDay')}
              </th>
              <th scope="col" style={{ textAlign: 'right', padding: '10px 8px', borderBottom: '1px solid #e5e7eb', minWidth: 110 }}>{t('warehouse.table.daysCover')}</th>
              <th scope="col" style={{ textAlign: 'right', padding: '10px 8px', borderBottom: '1px solid #e5e7eb', minWidth: 150 }}>{t('warehouse.table.reorder')}</th>
              <th scope="col" style={{ textAlign: 'right', padding: '10px 8px', borderBottom: '1px solid #e5e7eb', minWidth: 170 }}>{t('warehouse.table.actions')}</th>
            </tr>
          </thead>
          <tbody>
            {tabItems.map((item) => {
              const daily = avgDailyKgFor(item)
              const days = daysOfCoverFor(item)
              const usageInfo = usageInfoFor(item)
              const reorder = reorderStatusFor(item)
              const estimatedOnHand = estimatedOnHandFor(item)
              const estimatedAnchor = estimatedOnHandAnchorFor(item)
              const estimatedTooltip = estimatedAnchor
                ? (estimatedAnchor.capped
                  ? t('warehouse.estimatedOnHandTooltipCapped', {
                    anchorDate: estimatedAnchor.anchorIso,
                    capDays: USAGE_FETCH_WINDOW_DAYS
                  })
                  : t('warehouse.estimatedOnHandTooltip', {
                    anchorDate: estimatedAnchor.anchorIso
                  }))
                : undefined
              const dates = readWarehouseDates(item)
              return (
                <tr key={item.id}>
                  <td style={{ padding: '10px 8px', borderBottom: '1px solid #f1f5f9' }}>
                    <div style={{ fontWeight: 600 }}>{item.name}</div>
                    {renderItemDetails(item)}
                    {usageInfo ? (
                      <div style={{ marginTop: 2, fontSize: 12, color: '#6b7280' }}>
                        {t('warehouse.linkedFeedTypeLabel')}{' '}
                        <span style={{ color: '#374151', fontWeight: 600 }}>
                          {usageInfo.displayFeedType}
                        </span>
                        {usageInfo.totalKgInWindow > 0
                          ? ` · ${t('warehouse.linkedFeedTypeUsageInWindow', {
                            totalKg: formatQty(usageInfo.totalKgInWindow, 2),
                            days: USAGE_RATE_WINDOW_DAYS
                          })}`
                          : ` · ${t('warehouse.linkedFeedTypeNoUsageInWindow', { days: USAGE_RATE_WINDOW_DAYS })}`}
                      </div>
                    ) : null}
                    {item.notes ? <div style={{ fontSize: 12, color: '#6b7280' }}>{item.notes}</div> : null}
                  </td>
                  <td style={{ padding: '10px 8px', borderBottom: '1px solid #f1f5f9' }}>{formatCategory(item.category)}</td>
                  <td style={{ padding: '10px 8px', borderBottom: '1px solid #f1f5f9', whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>
                    {dates.receivedOn ?? '—'}
                  </td>
                  <td style={{ padding: '10px 8px', borderBottom: '1px solid #f1f5f9', whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>
                    {dates.expiresOn ?? '—'}
                  </td>
                  <td style={{ padding: '10px 8px', borderBottom: '1px solid #f1f5f9', textAlign: 'right', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
                    {isMixItem(item) ? '—' : `${formatQty(item.onHand, 3)} ${item.unit}`}
                  </td>
                  <td data-testid={`warehouse-unit-cost-${item.id}`} style={{ padding: '10px 8px', borderBottom: '1px solid #f1f5f9', textAlign: 'right', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
                    {isMixItem(item) ? '—' : formatUnitCost(item)}
                  </td>
                  <td
                    style={{ padding: '10px 8px', borderBottom: '1px solid #f1f5f9', textAlign: 'right', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}
                    title={estimatedTooltip}
                  >
                    {estimatedOnHand === null ? '—' : `${formatQty(estimatedOnHand, 2)} ${item.unit}${estimatedAnchor?.capped ? ' *' : ''}`}
                  </td>
                  <td style={{ padding: '10px 8px', borderBottom: '1px solid #f1f5f9', textAlign: 'right', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
                    {daily === null ? '—' : `${formatQty(daily, 2)} kg`}
                  </td>
                  <td style={{ padding: '10px 8px', borderBottom: '1px solid #f1f5f9', textAlign: 'right', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
                    {days === null ? '—' : days.toFixed(1)}
                  </td>
                  <td style={{ padding: '10px 8px', borderBottom: '1px solid #f1f5f9', textAlign: 'right' }}>
                    {item.reorderPoint === undefined ? '—' : `${formatQty(item.reorderPoint, 2)} ${item.unit}`}
                    {reorder.shouldReorder ? (
                      <div
                        style={{ marginTop: 4, fontSize: 12, fontWeight: 700 }}
                        title={t('warehouse.reorderNowTooltip')}
                      >
                        {t('warehouse.reorderNow')}
                      </div>
                    ) : null}
                    {reorderTaskActionFor(item, reorder)}
                  </td>
                  <td style={{ padding: '10px 8px', borderBottom: '1px solid #f1f5f9', textAlign: 'right' }}>
                    {renderItemActions(item)}
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
    )
  })()

  const renderReceiptHistory = (item: InventoryItem | null) => {
    if (!item) return null
    const receipts = readReceipts(item)
    if (receipts.length === 0) return null
    return (
      <div style={{ marginTop: 16 }} data-testid="warehouse-receipt-history">
        <div style={{ fontSize: 12, fontWeight: 700, color: '#374151', marginBottom: 6 }}>{t('warehouse.receive.history')}</div>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
          <thead>
            <tr>
              <th scope="col" style={{ textAlign: 'left', padding: '4px 6px', borderBottom: '1px solid #e5e7eb' }}>{t('warehouse.receive.date')}</th>
              <th scope="col" style={{ textAlign: 'right', padding: '4px 6px', borderBottom: '1px solid #e5e7eb' }}>{t('warehouse.receive.quantity')}</th>
              <th scope="col" style={{ textAlign: 'left', padding: '4px 6px', borderBottom: '1px solid #e5e7eb' }}>{t('warehouse.receive.lotNumber')}</th>
              <th scope="col" style={{ textAlign: 'left', padding: '4px 6px', borderBottom: '1px solid #e5e7eb' }}>{t('warehouse.receive.supplier')}</th>
            </tr>
          </thead>
          <tbody>
            {receipts.slice().reverse().map((receipt) => (
              <tr key={receipt.id}>
                <td style={{ padding: '4px 6px', borderBottom: '1px solid #f1f5f9', whiteSpace: 'nowrap' }}>{receipt.date}</td>
                <td style={{ padding: '4px 6px', borderBottom: '1px solid #f1f5f9', textAlign: 'right', whiteSpace: 'nowrap' }}>{formatQty(receipt.quantity, 3)} {item.unit}</td>
                <td style={{ padding: '4px 6px', borderBottom: '1px solid #f1f5f9' }}>{receipt.lotNumber ?? '—'}</td>
                <td style={{ padding: '4px 6px', borderBottom: '1px solid #f1f5f9' }}>{receipt.supplier ?? '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    )
  }

  const noticeStyle = (tone: NoticeTone): React.CSSProperties => {
    if (tone === 'error') return { border: '1px solid #b91c1c', background: '#ffffff', color: '#b91c1c' }
    if (tone === 'success') return { border: '1px solid #0f766e', background: '#ffffff', color: '#0f766e' }
    return { border: '1px solid #cbd5f5', background: '#ffffff', color: '#0f172a' }
  }

  return (
    <PageShell style={{ maxWidth: 1120 }}>
      <div data-screen="warehouse">
        <div className="app-header" style={{ padding: 0 }}>
          <div className="app-header-inner">
            <div className="app-header-top">
              <div className="app-header-title">
                <h1>{t('warehouse.title')}</h1>
                <p>{t('warehouse.subtitle')}</p>
              </div>

              <div className="app-header-actions">
                <div className="app-header-links" style={{ marginTop: 0 }}>
                  <button
                    type="button"
                    className="app-header-link"
                    onClick={goToFeed}
                    disabled={!hasSelectedFarm}
                    title={hasSelectedFarm ? undefined : t('warehouse.selectFacilityFirst')}
                  >
                    {t('nav.feed')}
                  </button>
                  <button
                    type="button"
                    className="app-header-link"
                    onClick={goToTasks}
                  >
                    {t('nav.tasks')}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        {notice && (notice.tone === 'error' || bannerVisibility.showActionMessage) && (
          notice.tone === 'error' ? (
            <div
              style={{
                ...noticeStyle(notice.tone),
                borderRadius: 12,
                padding: '12px 14px',
                marginTop: 12,
                display: 'flex',
                gap: 12,
                alignItems: 'flex-start',
                justifyContent: 'space-between'
              }}
              role="alert"
            >
              <div style={{ fontSize: 14, lineHeight: 1.4 }}>{notice.message}</div>
              <button
                type="button"
                className="app-header-link"
                onClick={() => setNotice(null)}
                aria-label="Dismiss"
                style={{ padding: '6px 10px', lineHeight: 1 }}
              >
                ×
              </button>
            </div>
          ) : (
            <output
              style={{
                ...noticeStyle(notice.tone),
                borderRadius: 12,
                padding: '12px 14px',
                marginTop: 12,
                display: 'flex',
                gap: 12,
                alignItems: 'flex-start',
                justifyContent: 'space-between'
              }}
              aria-live="polite"
            >
              <span style={{ fontSize: 14, lineHeight: 1.4 }}>{notice.message}</span>
              <button
                type="button"
                className="app-header-link"
                onClick={() => setNotice(null)}
                aria-label="Dismiss"
                style={{ padding: '6px 10px', lineHeight: 1 }}
              >
                ×
              </button>
            </output>
          )
        )}

        <CurrentContextBar
          style={{ marginTop: 14 }}
          fields={[
            { label: t('warehouse.contextFacility'), value: selectedFarmLabel }
          ]}
        />

        {(syncAttentionMessage || syncLastResultSummary) ? (
          <div
            className="form-card"
            style={{
              marginTop: 14,
              borderColor: '#93c5fd',
              background: '#eff6ff',
              color: '#1e3a8a'
            }}
            aria-live="polite"
            role="status"
          >
            {syncAttentionMessage ? <p style={{ margin: 0 }}>{syncAttentionMessage}</p> : null}
            {syncLastResultSummary ? (
              <p style={{ margin: syncAttentionMessage ? '8px 0 0 0' : 0, fontSize: 13 }}>
                {t('warehouse.sync.lastResult', { result: syncLastResultSummary.resultLabel })}
                {' '}
                {t('warehouse.sync.lastAttempted', { time: syncLastResultSummary.attemptedAtLabel })}
              </p>
            ) : null}
            {syncAttentionMessage ? (
              <div style={{ display: 'flex', gap: 8, marginTop: 10, flexWrap: 'wrap' }}>
                <GradientButton
                  type="button"
                  variant="info"
                  onClick={() => void handleInventorySyncRetry()}
                  disabled={retryingSync || refreshingSync || itemsLoading}
                >
                  {retryingSync ? t('warehouse.sync.retrying') : t('warehouse.sync.retryAction')}
                </GradientButton>
                <GradientButton
                  type="button"
                  variant="secondary"
                  onClick={() => void handleInventorySyncRefresh()}
                  disabled={refreshingSync || retryingSync || itemsLoading}
                >
                  {t('warehouse.sync.refreshAction')}
                </GradientButton>
              </div>
            ) : null}
          </div>
        ) : null}

        <div className="form-card" style={{ marginTop: 14 }}>
          <ContextSelectorBar
            farm={{
              label: t('warehouse.contextFacility'),
              value: farmId,
              options: farmOptions,
              onChange: (value) => {
                setFarmId(value)
                resetDraft()
                setPendingDeleteId(null)
              },
              placeholder: farms.length === 0 ? t('warehouse.loadingFacilities') : t('warehouse.selectFacility'),
              disabled: farms.length === 0
            }}
            summary={
              <div style={{ fontSize: 12, color: '#6b7280', paddingBottom: 8 }}>
                {t('warehouse.derivationWindowPrefix')} {USAGE_RATE_WINDOW_DAYS} {t('warehouse.derivationWindowSuffix')} ·{' '}
                {t('warehouse.derivationWindowFormula', { capDays: USAGE_FETCH_WINDOW_DAYS })}
                {usageLoading ? ` · ${t('feed.status.loading')}` : ''}
                {usageError ? ` · ${usageError}` : ''}
              </div>
            }
          />
        </div>

        <Drawer
          open={formOpen}
          onClose={resetDraft}
          title={editingId ? t('warehouse.form.editTitle') : t('warehouse.form.addTitle')}
        >
        <div data-testid="warehouse-item-form">

          <div className="responsive-toolbar" style={{ alignItems: 'flex-end' }}>
            <div className="field" style={{ minWidth: 240 }}>
              <FormField
                id="warehouse-item-name"
                label={t('warehouse.field.name')}
                value={draftName}
                placeholder={t('warehouse.placeholder.itemName')}
                disabled={!canEditInventory || !farmId}
                error={draftErrors.name}
                onChange={(value) => setDraftName(String(value))}
              />
            </div>

            <div className="field" style={{ minWidth: 180 }}>
              <FormField
                id="warehouse-item-category"
                type="select"
                label={t('warehouse.field.category')}
                value={draftCategory}
                disabled={!canEditInventory || !farmId}
                options={[
                  { value: 'feed', label: t('warehouse.category.feed') },
                  { value: 'chemical', label: t('warehouse.category.chemical') },
                  { value: 'equipment', label: t('warehouse.category.equipment') },
                  { value: 'other', label: t('warehouse.category.other') }
                ]}
                onChange={(value) => setDraftCategory(String(value) as InventoryCategory)}
              />
            </div>

            <div className="field" style={{ minWidth: 140 }}>
              <FormField
                id="warehouse-item-unit"
                type="select"
                label={t('warehouse.field.unit')}
                value={draftUnit}
                disabled={!canEditInventory || !farmId}
                options={[
                  { value: 'kg', label: 'kg' },
                  { value: 'l', label: 'L' },
                  { value: 'pcs', label: 'pcs' }
                ]}
                onChange={(value) => setDraftUnit(String(value) as InventoryUnit)}
              />
            </div>

            <div className="field" style={{ minWidth: 160 }}>
              <FormField
                id="warehouse-item-onhand"
                label={t('warehouse.field.onHand')}
                value={draftOnHand}
                placeholder="0"
                disabled={!canEditInventory || !farmId}
                error={draftErrors.onHand}
                onChange={(value) => setDraftOnHand(String(value))}
              />
            </div>

            <div className="field" style={{ minWidth: 220 }}>
              <FormField
                id="warehouse-item-reorder"
                label={t('warehouse.field.reorderPoint')}
                value={draftReorderPoint}
                placeholder=""
                disabled={!canEditInventory || !farmId}
                error={draftErrors.reorderPoint}
                hint={t('warehouse.help.reorderPoint')}
                onChange={(value) => setDraftReorderPoint(String(value))}
              />
            </div>

            <div className="field" style={{ minWidth: 200 }}>
              <label htmlFor="warehouse-item-received" style={{ fontSize: 12, fontWeight: 600, color: '#374151' }}>
                {t('warehouse.field.receivedOn')} <span aria-hidden="true">*</span>
              </label>
              <DateInput
                id="warehouse-item-received"
                value={draftReceivedOn}
                onChangeValue={setDraftReceivedOn}
                disabled={!canEditInventory || !farmId}
                pickerOnly
                style={{ width: '100%', padding: '10px 12px', border: `1px solid ${draftErrors.receivedOn ? '#ef4444' : '#d1d5db'}`, borderRadius: 10 }}
              />
              {draftErrors.receivedOn ? (
                <div style={{ marginTop: 6, fontSize: 12, color: '#ef4444' }}>{draftErrors.receivedOn}</div>
              ) : null}
            </div>

            <div className="field" style={{ minWidth: 220 }}>
              <label htmlFor="warehouse-item-expires" style={{ fontSize: 12, fontWeight: 600, color: '#374151' }}>
                {t('warehouse.field.expiresOnOptional')}
              </label>
              <DateInput
                id="warehouse-item-expires"
                value={draftExpiresOn}
                onChangeValue={setDraftExpiresOn}
                disabled={!canEditInventory || !farmId}
                pickerOnly
                style={{ width: '100%', padding: '10px 12px', border: `1px solid ${draftErrors.expiresOn ? '#ef4444' : '#d1d5db'}`, borderRadius: 10 }}
              />
              {draftErrors.expiresOn ? (
                <div style={{ marginTop: 6, fontSize: 12, color: '#ef4444' }}>{draftErrors.expiresOn}</div>
              ) : (
                <div style={{ marginTop: 6, fontSize: 12, color: '#6b7280' }}>{t('warehouse.help.expiresOn')}</div>
              )}
            </div>
          </div>

          <div className="responsive-toolbar" style={{ marginTop: 10, alignItems: 'flex-end' }}>
            {/* findings #105: the feed-type key only applies to feeds; the price applies to every item. */}
            {draftCategory === 'feed' ? (
            <div className="field" style={{ minWidth: 280 }}>
              <div>
                <label htmlFor="warehouse-item-feedtype" style={{ fontSize: 12, fontWeight: 600, color: '#374151' }}>
                  {t('warehouse.field.feedTypeKey')}
                </label>
                <input
                  id="warehouse-item-feedtype"
                  value={draftUsageFeedType}
                  onChange={(event) => setDraftUsageFeedType(event.target.value)}
                  placeholder={t('warehouse.placeholder.feedTypeKey')}
                  disabled={!canEditInventory || !farmId || draftCategory !== 'feed'}
                  list={draftCategory === 'feed' && suggestedFeedTypes.length > 0 ? 'warehouse-feedtype-suggestions' : undefined}
                  style={{ width: '100%', padding: '10px 12px', border: '1px solid #d1d5db', borderRadius: 10 }}
                />
                {draftCategory === 'feed' && suggestedFeedTypes.length > 0 ? (
                  <datalist id="warehouse-feedtype-suggestions">
                    {suggestedFeedTypes.map((feedType) => (
                      <option key={feedType} value={feedType} />
                    ))}
                  </datalist>
                ) : null}
                <div style={{ marginTop: 6, fontSize: 12, color: '#6b7280' }}>
                  {t('warehouse.help.feedTypeKey')}
                  {draftCategory === 'feed' && suggestedFeedTypes.length > 0
                    ? ` ${t('warehouse.help.feedTypeKeySuggestions', { days: USAGE_RATE_WINDOW_DAYS })}`
                    : ''}
                </div>
              </div>
            </div>
            ) : null}

            <>
                <div className="field" style={{ minWidth: 180 }}>
                  <label htmlFor="warehouse-item-unit-cost" style={{ fontSize: 12, fontWeight: 600, color: '#374151' }}>
                    {t('inventory.form.unitCost')}
                  </label>
                  <input
                    id="warehouse-item-unit-cost"
                    value={draftUnitCostMajor}
                    onChange={(event) => setDraftUnitCostMajor(event.target.value)}
                    placeholder="0.00"
                    inputMode="decimal"
                    disabled={!canEditInventory || !farmId}
                    style={{ width: '100%', padding: '10px 12px', border: `1px solid ${draftErrors.unitCost ? '#ef4444' : '#d1d5db'}`, borderRadius: 10 }}
                  />
                  {draftErrors.unitCost ? (
                    <div style={{ marginTop: 6, fontSize: 12, color: '#ef4444' }}>{draftErrors.unitCost}</div>
                  ) : (
                    <div style={{ marginTop: 6, fontSize: 12, color: '#6b7280' }}>{t(draftCategory === 'feed' ? 'inventory.form.unitCostHelp' : draftCategory === 'chemical' ? 'inventory.form.unitCostHelpChemical' : 'inventory.form.unitCostHelpOther')}</div>
                  )}
                </div>
                <div className="field" style={{ minWidth: 120 }}>
                  <label htmlFor="warehouse-item-currency" style={{ fontSize: 12, fontWeight: 600, color: '#374151' }}>
                    {t('inventory.form.currency')}
                  </label>
                  <input
                    id="warehouse-item-currency"
                    value={draftUnitCostCurrency}
                    onChange={(event) => setDraftUnitCostCurrency(event.target.value.toUpperCase())}
                    placeholder="USD"
                    maxLength={3}
                    disabled={!canEditInventory || !farmId}
                    style={{ width: '100%', padding: '10px 12px', border: `1px solid ${draftErrors.currency ? '#ef4444' : '#d1d5db'}`, borderRadius: 10, textTransform: 'uppercase' }}
                  />
                  {draftErrors.currency ? (
                    <div style={{ marginTop: 6, fontSize: 12, color: '#ef4444' }}>{draftErrors.currency}</div>
                  ) : null}
                </div>
              </>

            <div className="field" style={{ minWidth: 320 }}>
              <FormField
                id="warehouse-item-notes"
                label={t('warehouse.field.notes')}
                value={draftNotes}
                placeholder=""
                disabled={!canEditInventory || !farmId}
                onChange={(value) => setDraftNotes(String(value))}
              />
            </div>

            <div className="responsive-actions" style={{ marginLeft: 'auto' }}>
              <GradientButton
                onClick={resetDraft}
                variant="secondary"
                size="sm"
              >
                {t('warehouse.button.cancel')}
              </GradientButton>

              <GradientButton onClick={saveDraft} variant="success" size="sm" disabled={!canEditInventory || !farmId}>
                {editingId ? t('warehouse.button.saveChanges') : t('warehouse.button.addItem')}
              </GradientButton>
            </div>
          </div>

          {editingId ? renderReceiptHistory(items.find((item) => item.id === editingId) ?? null) : null}
        </div>
        </Drawer>

        <Drawer
          open={receiveItem !== null}
          onClose={closeReceive}
          title={receiveItem ? t('warehouse.receive.title', { item: receiveItem.name }) : t('warehouse.button.receive')}
        >
          {receiveItem ? (
            <div data-testid="warehouse-receive-form" style={{ display: 'grid', gap: 12 }}>
              <div className="field">
                <label htmlFor="warehouse-receive-date" style={{ fontSize: 12, fontWeight: 600, color: '#374151' }}>
                  {t('warehouse.receive.date')} <span aria-hidden="true">*</span>
                </label>
                <DateInput
                  id="warehouse-receive-date"
                  value={receiveDate}
                  onChangeValue={setReceiveDate}
                  pickerOnly
                  style={{ width: '100%', padding: '10px 12px', border: '1px solid #d1d5db', borderRadius: 10 }}
                />
              </div>
              <FormField
                id="warehouse-receive-quantity"
                label={`${t('warehouse.receive.quantity')} (${receiveItem.unit}) *`}
                value={receiveQuantity}
                placeholder="0"
                onChange={(value) => setReceiveQuantity(String(value))}
              />
              <FormField
                id="warehouse-receive-supplier"
                label={t('warehouse.receive.supplier')}
                value={receiveSupplier}
                placeholder=""
                onChange={(value) => setReceiveSupplier(String(value))}
              />
              <FormField
                id="warehouse-receive-lot"
                label={t('warehouse.receive.lotNumber')}
                value={receiveLotNumber}
                placeholder=""
                hint={receiveItem.category === 'feed' ? t('warehouse.receive.lotNumberHelp') : undefined}
                onChange={(value) => setReceiveLotNumber(String(value))}
              />
              <div style={{ display: 'flex', gap: 10 }}>
                <div className="field" style={{ flex: 2 }}>
                  <FormField
                    id="warehouse-receive-unit-cost"
                    label={t('inventory.form.unitCost')}
                    value={receiveUnitCost}
                    placeholder="0.00"
                    onChange={(value) => setReceiveUnitCost(String(value))}
                  />
                </div>
                <div className="field" style={{ flex: 1 }}>
                  <FormField
                    id="warehouse-receive-currency"
                    label={t('inventory.form.currency')}
                    value={receiveCurrency}
                    placeholder="USD"
                    onChange={(value) => setReceiveCurrency(String(value).toUpperCase())}
                  />
                </div>
              </div>
              <div className="field">
                <label htmlFor="warehouse-receive-expires" style={{ fontSize: 12, fontWeight: 600, color: '#374151' }}>
                  {t('warehouse.field.expiresOnOptional')}
                </label>
                <DateInput
                  id="warehouse-receive-expires"
                  value={receiveExpiresOn}
                  onChangeValue={setReceiveExpiresOn}
                  pickerOnly
                  style={{ width: '100%', padding: '10px 12px', border: '1px solid #d1d5db', borderRadius: 10 }}
                />
              </div>
              <div style={{ fontSize: 12, color: '#6b7280' }}>{t('warehouse.receive.stockHelp')}</div>
              {receiveConfirming ? (
                <div
                  data-testid="warehouse-receive-summary"
                  style={{ padding: 12, borderRadius: 10, border: '1px solid #f59e0b', background: '#fffbeb', color: '#92400e', fontSize: 13 }}
                >
                  <div style={{ fontWeight: 800, marginBottom: 4 }}>{t('warehouse.receive.summaryTitle', { farm: selectedFarmLabel })}</div>
                  <div>
                    {[
                      `${receiveQuantity.trim()} ${receiveItem.unit} · ${receiveItem.name}`,
                      toOptionalIsoDate(receiveDate) ?? '',
                      receiveSupplier.trim() ? t('warehouse.receive.summarySupplier', { supplier: receiveSupplier.trim() }) : '',
                      receiveLotNumber.trim() ? t('warehouse.receive.summaryLot', { lot: receiveLotNumber.trim() }) : ''
                    ].filter(Boolean).join(' · ')}
                  </div>
                </div>
              ) : null}
              {receiveError ? (
                <div role="alert" data-testid="warehouse-receive-error" style={{ fontSize: 13, color: '#b91c1c', fontWeight: 700 }}>{receiveError}</div>
              ) : null}
              <div className="responsive-actions" style={{ justifyContent: 'flex-end' }}>
                <GradientButton variant="secondary" size="sm" onClick={closeReceive}>
                  {t('warehouse.button.cancel')}
                </GradientButton>
                {receiveConfirming ? (
                  <GradientButton variant="secondary" size="sm" onClick={() => setReceiveConfirming(false)} data-testid="warehouse-receive-back">
                    {t('warehouse.receive.back')}
                  </GradientButton>
                ) : null}
                <GradientButton
                  variant="success"
                  size="sm"
                  onClick={() => {
                    if (!receiveConfirming) {
                      const quantity = parseDecimal(receiveQuantity)
                      if (!toOptionalIsoDate(receiveDate)) {
                        setReceiveError(t('warehouse.receive.error.date'))
                        return
                      }
                      if (quantity === null || quantity <= 0) {
                        setReceiveError(t('warehouse.receive.error.quantity'))
                        return
                      }
                      setReceiveError(null)
                      setReceiveConfirming(true)
                      return
                    }
                    void saveReceive()
                  }}
                  disabled={!canEditInventory || receiveSaving}
                  data-testid="warehouse-receive-save"
                >
                  {receiveConfirming ? t('warehouse.receive.confirm') : t('warehouse.receive.save')}
                </GradientButton>
              </div>
              {renderReceiptHistory(receiveItem)}
            </div>
          ) : null}
        </Drawer>

        <Drawer
          open={mixOpen}
          onClose={closeMix}
          title={mixEditingId ? t('warehouse.mix.editTitle') : t('warehouse.mix.title')}
        >
          <div data-testid="warehouse-mix-form" style={{ display: 'grid', gap: 12 }}>
            <div style={{ fontSize: 13, color: '#475569' }}>{t('warehouse.mix.help')}</div>
            <FormField
              id="warehouse-mix-name"
              label={`${t('warehouse.mix.name')} *`}
              value={mixName}
              placeholder={t('warehouse.mix.namePlaceholder')}
              onChange={(value) => setMixName(String(value))}
            />
            {mixComponentOptions.length < 2 ? (
              <div style={{ fontSize: 13, color: '#b91c1c' }}>{t('warehouse.mix.needTwoFeeds')}</div>
            ) : null}
            {mixRows.map((row, index) => (
              <div key={index} style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }}>
                <div className="field" style={{ flex: 3 }}>
                  <FormField
                    id={`warehouse-mix-feed-${index}`}
                    type="select"
                    label={t('warehouse.mix.feed', { n: index + 1 })}
                    value={row.itemId}
                    placeholder={t('warehouse.mix.chooseFeed')}
                    options={mixComponentOptions.map((item) => ({ value: item.id, label: item.name }))}
                    onChange={(value) => setMixRows((prev) => prev.map((candidate, i) => (i === index ? { ...candidate, itemId: String(value) } : candidate)))}
                  />
                </div>
                <div className="field" style={{ flex: 1 }}>
                  <FormField
                    id={`warehouse-mix-percent-${index}`}
                    label="%"
                    value={row.percent}
                    placeholder="0"
                    onChange={(value) => setMixRows((prev) => prev.map((candidate, i) => (i === index ? { ...candidate, percent: String(value) } : candidate)))}
                  />
                </div>
                {mixRows.length > 2 ? (
                  <GradientButton
                    variant="secondary"
                    size="xs"
                    onClick={() => setMixRows((prev) => prev.filter((_, i) => i !== index))}
                    aria-label={t('warehouse.mix.removeFeed')}
                    title={t('warehouse.mix.removeFeed')}
                  >
                    ×
                  </GradientButton>
                ) : null}
              </div>
            ))}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
              <GradientButton
                variant="secondary"
                size="xs"
                onClick={() => setMixRows((prev) => [...prev, { itemId: '', percent: '' }])}
              >
                {t('warehouse.mix.addFeed')}
              </GradientButton>
              <span
                data-testid="warehouse-mix-total"
                style={{ fontWeight: 800, color: Math.abs(mixPercentTotal - 100) < 0.001 ? '#047857' : '#b45309' }}
              >
                {t('warehouse.mix.total', { total: formatQty(mixPercentTotal, 2) })}
              </span>
            </div>
            <FormField
              id="warehouse-mix-notes"
              label={t('warehouse.field.notes')}
              value={mixNotes}
              placeholder=""
              onChange={(value) => setMixNotes(String(value))}
            />
            <div style={{ fontSize: 12, color: '#6b7280' }}>{t('warehouse.mix.stockHelp')}</div>
            {mixError ? (
              <div role="alert" data-testid="warehouse-mix-error" style={{ fontSize: 13, color: '#b91c1c', fontWeight: 700 }}>{mixError}</div>
            ) : null}
            <div className="responsive-actions" style={{ justifyContent: 'flex-end' }}>
              <GradientButton variant="secondary" size="sm" onClick={closeMix}>
                {t('warehouse.button.cancel')}
              </GradientButton>
              <GradientButton
                variant="success"
                size="sm"
                onClick={() => void saveMix()}
                disabled={!canEditInventory || !farmId}
                data-testid="warehouse-mix-save"
              >
                {t('warehouse.mix.save')}
              </GradientButton>
            </div>
          </div>
        </Drawer>

        <div className="form-card" style={{ marginTop: 14 }}>
          <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
            <h3 style={{ marginTop: 0 }}>{t('warehouse.section.inventory')}</h3>
            {itemsLoading ? <div style={{ fontSize: 12, color: '#6b7280' }}>{t('feed.status.loading')}</div> : null}
            <div className="responsive-actions" style={{ marginLeft: 'auto' }}>
              {categoryTab === 'feed' ? (
                <GradientButton
                  variant="secondary"
                  size="sm"
                  onClick={openNewMix}
                  disabled={!canEditInventory || !farmId}
                  data-testid="warehouse-prepare-mix"
                >
                  {t('warehouse.mix.prepare')}
                </GradientButton>
              ) : null}
              <GradientButton
                variant="success"
                size="sm"
                onClick={openNewItem}
                disabled={!canEditInventory || !farmId}
                data-testid="warehouse-add-item"
              >
                {t('warehouse.button.addItem')}
              </GradientButton>
            </div>
          </div>

          <div role="tablist" aria-label={t('warehouse.field.category')} style={{ display: 'flex', gap: 6, flexWrap: 'wrap', margin: '4px 0 12px' }}>
            {CATEGORY_TABS.map((category) => {
              const active = categoryTab === category
              const count = visibleItems.filter((item) => item.category === category).length
              return (
                <button
                  key={category}
                  type="button"
                  role="tab"
                  aria-selected={active}
                  data-testid={`warehouse-tab-${category}`}
                  onClick={() => {
                    setCategoryTab(category)
                    setPendingDeleteId(null)
                  }}
                  style={{
                    minHeight: 40,
                    padding: '6px 14px',
                    borderRadius: 999,
                    border: active ? '2px solid #1d4ed8' : '1px solid #d1d5db',
                    background: active ? '#eff6ff' : '#ffffff',
                    color: '#0f172a',
                    fontWeight: active ? 800 : 600,
                    cursor: 'pointer'
                  }}
                >
                  {formatCategory(category)} ({count})
                </button>
              )
            })}
          </div>

          {itemsError ? (
            <div style={{ fontSize: 14, color: '#b91c1c' }}>{itemsError}</div>
          ) : null}

          {inventoryContent}
        </div>
      </div>
    </PageShell>
  )
}
